import { tvpil } from '../../../data/compat/tvpil';
import type { WatchedAsset } from '../../../data/continueWatching/types';
import { KalturaWatchedAsset } from '../../composed';
import { KalturaWatchStatus } from '../../enums';
import {
  createAssetHistoryFilter,
  createOnDemandResponseProfile,
  createPager,
  createSearchAssetFilter,
} from '../../factories';
import { KalturaAssetHistory } from '../../objects';
import { processWatchedAsset } from '../../processing/continueWatching';
import { fetchAssetHistory, fetchAssetList } from '../../services';

import { assetRetrievedProperties, createAssetHistoryResponseProfile } from './utils';

export type FetchWatchedAssetsArgs = {
  daysSinceWatched?: number;
  kSql: string;
  pageIndex: number;
  pageSize: number;
  enableEpisodesGrouping: boolean;
  typeIn: string;
  watchingStatus: KalturaWatchStatus;
};

export async function fetchWatchedAssets({
  daysSinceWatched,
  kSql,
  pageIndex,
  pageSize,
  enableEpisodesGrouping,
  typeIn,
  watchingStatus,
}: FetchWatchedAssetsArgs) {
  const { objects: assetHistories } = await fetchAssetHistoryList({
    daysSinceWatched,
    enableEpisodesGrouping,
    pageIndex,
    pageSize,
    typeIn,
    watchingStatus,
  });

  if (!assetHistories.length) {
    return {
      objects: [],
      totalCount: 0,
    };
  }

  const historyKsql = tvpil.addKsql(
    kSql,
    `(or ${assetHistories.map((asset) => `${asset.assetType}_id='${asset.assetId}'`).join(' ')} )`,
  );

  const { objects: assets, totalCount } = await fetchAssetListByHistory({
    typeIn,
    historyKsql,
  });

  const assetsMap = createAssetsMap(assets);

  const watchedAssets: WatchedAsset[] = [];

  assetHistories.forEach((assetHistory) => {
    const asset = assetsMap.get(assetHistory.assetId);

    if (asset) {
      watchedAssets.push(
        processWatchedAsset({
          assetHistory,
          watchedAsset: asset,
        }),
      );
    }
  });

  return {
    objects: watchedAssets,
    totalCount,
  };
}

async function fetchAssetHistoryList({
  daysSinceWatched,
  enableEpisodesGrouping,
  pageIndex,
  pageSize,
  typeIn,
  watchingStatus,
}: {
  daysSinceWatched?: number;
  enableEpisodesGrouping: boolean;
  pageIndex: number;
  pageSize: number;
  typeIn: string;
  watchingStatus: KalturaWatchStatus;
}) {
  return fetchAssetHistory<KalturaAssetHistory>({
    filter: createAssetHistoryFilter({
      typeIn,
      statusEqual: watchingStatus,
      daysLessThanOrEqual: daysSinceWatched,
    }),
    pager: createPager({
      pageIndex,
      pageSize,
    }),
    responseProfile: createAssetHistoryResponseProfile({ enableEpisodesGrouping }),
  });
}

function fetchAssetListByHistory({ historyKsql, typeIn }: { historyKsql: string; typeIn: string }) {
  return fetchAssetList<KalturaWatchedAsset>({
    filter: createSearchAssetFilter({
      typeIn,
      kSql: historyKsql,
    }),
    responseProfile: createOnDemandResponseProfile({
      retrievedProperties: assetRetrievedProperties,
    }),
  });
}

function createAssetsMap(assets: KalturaWatchedAsset[]) {
  const assetsMap = new Map<number, KalturaWatchedAsset>();

  for (let i = 0; i < assets.length; i++) {
    const asset = assets[i];

    if (asset.objectType !== 'KalturaRecordingAsset') {
      assetsMap.set(asset.id, asset);
    } else {
      assetsMap.set(Number(asset.recordingId), asset);
    }
  }

  return assetsMap;
}
